import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import purchaseOrderAPI from '../../api/purchaseOrderAPI';
import { formatDate } from '../../utils/dateUtils';
import { formatNumber } from '../../utils/numberUtils';
import DateEditor from '../../components/DateEditor'; 
import ReturnModal from '../../components/ReturnModal';
import ThreeWayMatchSummary from '../../components/ThreeWayMatchSummary';
import SupplierInvoiceForm from '../../components/SupplierInvoiceForm';
import MatchResultBanner from '../../components/MatchResultBanner';
import CreditNoteApprovalCard from '../../components/CreditNoteApprovalCard';
import { useAuth } from '../../context/AuthContext';

const PurchaseOrderDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { selectedClient } = useAuth();
    const [po, setPo] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingItemId, setEditingItemId] = useState(null);
    const [receivedQuantities, setReceivedQuantities] = useState({});

    const [isEditingDate, setIsEditingDate] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [showReturnModal, setShowReturnModal] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [returnHistory, setReturnHistory] = useState([]);

    // 3-Way Match states
    const [activeTab, setActiveTab] = useState('items'); // 'items', 'returns', 'match'
    const [tenant, setTenant] = useState(null);
    const [showSupplierInvoiceModal, setShowSupplierInvoiceModal] = useState(false);
    const [matchResult, setMatchResult] = useState(null);
    const [showMatchResultBanner, setShowMatchResultBanner] = useState(false);
    const [hasExistingInvoice, setHasExistingInvoice] = useState(false);
    const [checkingInvoice, setCheckingInvoice] = useState(false);
    const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);
    const [creditNoteId, setCreditNoteId] = useState(null);
    const [summaryRefreshKey, setSummaryRefreshKey] = useState(0);

    // Add this inside your component file, outside the component function
    const printStyles = `
        @media print {
        .sidebar {
            display: none !important;
        }
        .main-content {
            margin-left: 0 !important;
            width: 100% !important;
        }
        .no-print {
            display: none !important;
        }
        body {
            background: white !important;
        }
        .print-area {
            padding: 0 !important;
            margin: 0 !important;
        }
        .date-edit-btn {
            display: none !important;
          }
        }
        `;

    useEffect(() => {
        
        fetchPOData();
    }, [id]);

    useEffect(() => {
        // Get tenant info from localStorage
        const tenantStr = localStorage.getItem('tenant');
        if (tenantStr) {
            try {
                setTenant(JSON.parse(tenantStr));
            } catch (err) {
                console.error('Failed to parse tenant info:', err);
            }
        }
    }, []);

    useEffect(() => {
        // Check if there's an existing supplier invoice for this PO
        if (po && tenant?.tenantType === 'retail') {
            checkExistingSupplierInvoice();
        }
    }, [po, tenant]);

    const checkExistingSupplierInvoice = async () => {
        try {
            setCheckingInvoice(true);
            const token = localStorage.getItem('authToken');
            const response = await fetch(`/api/supplier-invoices?poId=${id}&limit=200`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await response.json();
            const invoices = data.data || data || [];
            const nonVoidInvoices = invoices.filter(si => si.status !== 'void');
            setHasExistingInvoice(nonVoidInvoices.length > 0);
        } catch (err) {
            console.error('Error checking supplier invoice:', err);
            setHasExistingInvoice(false);
        } finally {
            setCheckingInvoice(false);
        }
    };

    // Add this useEffect to inject print styles
    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.innerHTML = printStyles;
        document.head.appendChild(styleElement);
        
        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    const fetchPOData = async () => {
        try { 
            setLoading(true);
            setError(null);
            
            const poResponse = await purchaseOrderAPI.getById(id);
            setPo(poResponse.data);
            
            const itemsResponse = await purchaseOrderAPI.getItems(id);
            setItems(itemsResponse.data);
            
            // Initialize received quantities
            const initialQuantities = {};
            itemsResponse.data.forEach(item => {
                initialQuantities[item._id] = item.receivedQuantity || 0;
            });
            setReceivedQuantities(initialQuantities);
            
        } catch (err) {
            setError(err.message || 'Failed to load purchase order');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Purchase Order ${po.poNumber}</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 40px 20px; padding: 20px; }
                        table { width: 100%; border-collapse: collapse; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                        th { background-color: #f2f2f2; }
                        .header { margin-bottom: 20px; }
                        .footer { margin-top: 20px; border-top: 1px solid #ddd; padding-top: 10px; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>PO Number: ${po.poNumber}</h1>
                        <p>Date: ${formatDate(po.orderDate)}</p>
                        <p>Supplier: ${po.supplier?.name || 'N/A'}</p>
                    </div>
                    
                    <table>
                        <thead>
                            <tr>
                                <th>Product</th>
                                <th>Barcode</th>
                                <th>Ordered</th>
                                <th>Received</th>
                                <th>Unit Cost</th>
                                <th>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${items.map(item => `
                                <tr>
                                    <td>${item.productName}</td>
                                    <td>${item.barcode || 'N/A'}</td>
                                    <td>${item.quantity}</td>
                                    <td>${item.receivedQuantity || 0} / ${item.quantity}</td>
                                    <td>${formatNumber(item.cost || 0)}</td>
                                    <td>${formatNumber(item.quantity * item.cost)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    
                    <div class="footer">
                        <p>Total: ${formatNumber(po.total)}</p>
                    </div>
                    
                    <script>
                        window.onload = function() {
                            setTimeout(function() {
                                window.print();
                                window.close();
                            }, 100);
                        }
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    // Update your receiveItem API call:
    const handleReceiveItem = async (itemId) => {
        try {
          setLoading(true);
          const newQuantity = Number(receivedQuantities[itemId]);
          const item = items.find(i => i._id === itemId);
      
          // Validate input
          if (isNaN(newQuantity)) {
            throw new Error("Please enter a valid number");
          }
          
          // Calculate the remaining quantity that can be received
          const remainingQuantity = item.quantity - (item.receivedQuantity || 0);
          
          if (newQuantity <= 0) {
            throw new Error("Quantity must be greater than 0");
          }
          
          if (newQuantity > remainingQuantity) {
            throw new Error(`Cannot receive more than ${remainingQuantity} remaining for this item`);
          }
      
          // Update UI optimistically
          const updatedItems = items.map(i => 
            i._id === itemId 
              ? { 
                  ...i, 
                  receivedQuantity: (i.receivedQuantity || 0) + newQuantity,
                  isReceived: (i.receivedQuantity || 0) + newQuantity >= i.quantity
                }
              : i
          );
          setItems(updatedItems);
      
          // Make API call - send just the new quantity being received now
          await purchaseOrderAPI.receiveItem(id, itemId, newQuantity);
      
          // Check if all items are received
          const allReceived = updatedItems.every(i => i.receivedQuantity >= i.quantity);
          if (allReceived) {
            await purchaseOrderAPI.receive(id);
            const updatedPo = await purchaseOrderAPI.getById(id);
            setPo(updatedPo.data);
          }
      
          // Reset the input field
          setReceivedQuantities({
            ...receivedQuantities,
            [itemId]: 0
          });
          
          setError(null);
        } catch (err) {
          // Revert UI on error
          setItems([...items]);
          setError(err.response?.data?.message || err.message);
        } finally {
          setLoading(false);
          setEditingItemId(null);
        }
      };

    const handleReceiveAll = async () => {
        try {
            setLoading(true);
            await purchaseOrderAPI.receive(id);
            
            // Update all items to received
            setItems(items.map(item => ({
                ...item,
                isReceived: true,
                receivedQuantity: item.quantity
            })));
            
            navigate('/purchase-order', { state: { message: 'PO marked as received' } });
        } catch (err) {
            setError(err.message || 'Failed to mark as received');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async () => {
        try {
            setLoading(true);
            await purchaseOrderAPI.cancel(id);
            navigate('/purchase-order', { state: { message: 'PO cancelled successfully' } });
        } catch (err) {
            setError(err.message || 'Failed to cancel PO');
        } finally {
            setLoading(false);
        }
    };

    const handleQuantityChange = (itemId, value) => {
        const numValue = Math.max(0, Math.min(
            Number(value),
            items.find(i => i._id === itemId)?.quantity || 0
        ));
        
        setReceivedQuantities({
            ...receivedQuantities,
            [itemId]: numValue
        });
    };

    const startEditing = (itemId) => {
        setEditingItemId(itemId);
    };

    const cancelEditing = () => {
        setEditingItemId(null);
    };

    const handleUpdateOrderDate = async (newDate) => {
        try {
          setIsSaving(true);
          await purchaseOrderAPI.updateDate(id, { orderDate: newDate });
          
          // Update local state
          setPo(prev => ({
            ...prev,
            orderDate: newDate
          }));
          
          setIsEditingDate(false);
          setError(null);
        } catch (err) {
          setError(err.message || 'Failed to update order date');
        } finally {
          setIsSaving(false);
        }
      };


      const handleReturnOrder = () => {
        // Prepare items for return modal
        const returnableItems = items
          .filter(item => (item.receivedQuantity || 0) > 0) // Only items that were received
          .map(item => ({
            itemId: item._id,
            quantity: 0,
            maxQuantity: item.receivedQuantity || 0,
            productName: item.productName,
            receivedQuantity: item.receivedQuantity || 0,
            returnedQuantity: item.returnedQuantity || 0,
            remainingQuantity: (item.receivedQuantity || 0) - (item.returnedQuantity || 0)
          }))
          .filter(item => item.remainingQuantity > 0); // Only items that can still be returned
      
        if (returnableItems.length === 0) {
          setError('No items available for return');
          return;
        }
      
        setSelectedItems(returnableItems);
        setShowReturnModal(true);
      };
      
      const handleProcessReturn = async (returnData) => {
        if (!window.confirm('Are you sure you want to process this return to supplier?')) {
          return;
        }
      
        try {
          setLoading(true);
          const result = await purchaseOrderAPI.returnItems(id, returnData);
          
          // Refresh data
          await fetchPOData();
          
          // Fetch return history
          const returnsResponse = await purchaseOrderAPI.getReturns(id);
          setReturnHistory(returnsResponse.data);
          
          setError(null);
          setShowReturnModal(false);
          alert(result.message || 'Return processed successfully');
        } catch (err) {
          setError(err.message || 'Failed to process return');
        } finally {
          setLoading(false);
        }
      };
      
      // Fetch return history when component loads
      useEffect(() => {
        if (po && po.status === 'Received') {
          purchaseOrderAPI.getReturns(id)
            .then(response => setReturnHistory(response.data))
            .catch(err => console.error('Failed to fetch return history:', err));
        }
      }, [po, id]);

    // Handle Supplier Invoice Form Success
    const handleSupplierInvoiceSuccess = (result) => {
        setMatchResult(result);
        setShowMatchResultBanner(true);
        setShowSupplierInvoiceModal(false);
        // Refresh the summary panel
        setSummaryRefreshKey(prev => prev + 1);
        // Check invoice status again
        checkExistingSupplierInvoice();
    };

    // Handle View Credit Note
    const handleViewCreditNote = (cnId) => {
        setCreditNoteId(cnId);
        setShowCreditNoteModal(true);
    };

    // Handle Credit Note Approval Complete
    const handleCreditNoteApproved = () => {
        setShowCreditNoteModal(false);
        setCreditNoteId(null);
        setShowMatchResultBanner(false);
        setSummaryRefreshKey(prev => prev + 1);
    };

    const isRetailTenant = tenant?.tenantType === 'retail';


    if (loading) {
        return (
            <Layout pageTitle="Loading...">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
                </div>
            </Layout>
        );
    }

    if (!po) {
        return (
            <Layout pageTitle="Not Found">
                <div className="bg-white rounded-lg shadow-md p-6 text-center">
                    <h2 className="text-xl font-semibold mb-4">Purchase Order Not Found</h2>
                    <button 
                        onClick={() => navigate('/purchase-order')}
                        className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600"
                    >
                        Back to List
                    </button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle={`${po.poNumber}`}>
            <div className="max-w-6xl mx-auto p-6 bg-gray-50">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold">Purchase Order Details</h1>
                    <div className="flex space-x-2">
                        {isRetailTenant &&
                            (po.status === 'Ordered' || po.status === 'Received') &&
                            !hasExistingInvoice && (
                                <button
                                    onClick={() => setShowSupplierInvoiceModal(true)}
                                    className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 transition-colors disabled:opacity-50 no-print"
                                    disabled={checkingInvoice}
                                >
                                    {checkingInvoice ? 'Checking...' : 'Enter Supplier Invoice'}
                                </button>
                            )}
                        <button
                            onClick={handlePrint}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50 no-print"
                            disabled={loading}
                        >
                            {loading ? 'Printing...' : 'Print Receipt'}
                        </button>
                        <button
                            onClick={() => navigate('/purchase-order')}
                            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
                        >
                            Back to List
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                {showMatchResultBanner && matchResult && (
                    <div className="mb-4">
                        <MatchResultBanner
                            matchResult={matchResult}
                            onViewCreditNote={handleViewCreditNote}
                        />
                    </div>
                )}

                {/* PO Header Section */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-1">
                            <p><span className="font-medium">PO Number:</span> {po.poNumber}</p>
                            <p>
                                <span className="font-medium">Status:</span>
                                <span className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold ${
                                    po.status === 'Draft' ? 'bg-gray-100 text-gray-800' :
                                    po.status === 'Ordered' ? 'bg-blue-100 text-blue-800' :
                                    po.status === 'Received' ? 'bg-green-100 text-green-800' :
                                    po.status === 'Returned' ? 'bg-orange-100 text-orange-800' :
                                    po.status === 'Partially_Returned' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                }`}>
                                    {po.status === 'Partially_Returned' ? 'Partially Returned' : po.status}
                                </span>
                            </p>
                            <div className="flex justify-between items-center">
                                <span className="font-medium">Date:</span>
                                <div className="flex items-center gap-2">
                                    {isEditingDate ? (
                                        <DateEditor
                                            date={po.orderDate}
                                            onSave={handleUpdateOrderDate}
                                            onCancel={() => setIsEditingDate(false)}
                                            loading={isSaving}
                                        />
                                    ) : (
                                        <>
                                            <span>{formatDate(po.orderDate)}</span>
                                            <button
                                                onClick={() => setIsEditingDate(true)}
                                                className="text-blue-600 hover:text-blue-800 text-sm date-edit-btn no-print"
                                                title="Edit date"
                                            >
                                                Edit
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            {po.expectedDeliveryDate && (
                                <p><span className="font-medium">Expected Delivery:</span> {formatDate(po.expectedDeliveryDate)}</p>
                            )}
                        </div>

                        <div>
                            <h3 className="font-semibold text-gray-700 mb-2">Supplier</h3>
                            <div className="space-y-1">
                                <p className="font-medium">{po.supplier?.name || 'N/A'}</p>
                                {po.supplier?.phone && <p>{po.supplier.phone}</p>}
                                {po.supplier?.email && <p>{po.supplier.email}</p>}
                                {po.isCashPurchase
                                    ? <p className="text-sm text-gray-500">Cash Purchase</p>
                                    : <p className="text-sm text-gray-500">Charge Purchase</p>}
                            </div>
                        </div>

                        <div>
                            <h3 className="font-semibold text-gray-700 mb-2">Store</h3>
                            <div className="space-y-1">
                                <p className="font-medium">{po.store?.name || 'N/A'}</p>
                                {po.store?.location && <p>{po.store.location}</p>}
                                <p>Created by: {po.cashierId?.name || 'System'}</p>
                            </div>
                        </div>
                    </div>
                    <div>Notes: {po.notes}</div>
                </div>

                {/* Tab Navigation (Retail only) */}
                {isRetailTenant && (
                    <div className="bg-white rounded-lg shadow-md mb-6 border-b">
                        <div className="flex">
                            <button
                                onClick={() => setActiveTab('items')}
                                className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
                                    activeTab === 'items'
                                        ? 'border-teal-500 text-teal-600'
                                        : 'border-transparent text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                PO Items
                            </button>
                            <button
                                onClick={() => setActiveTab('returns')}
                                className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
                                    activeTab === 'returns'
                                        ? 'border-teal-500 text-teal-600'
                                        : 'border-transparent text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                Return Orders
                            </button>
                            <button
                                onClick={() => setActiveTab('match')}
                                className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
                                    activeTab === 'match'
                                        ? 'border-teal-500 text-teal-600'
                                        : 'border-transparent text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                3-Way Match
                            </button>
                        </div>
                    </div>
                )}

                {/* PO Items Section */}
                {(!isRetailTenant || activeTab === 'items') && (
                    <>
                        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                            <h2 className="text-xl font-semibold mb-4">PO Items</h2>

                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Barcode</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ordered</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Received</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Cost</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {items.map((item) => (
                                            <tr key={item._id}>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="font-medium">{item.productName}</div>
                                                    <div className="text-sm text-gray-500">{item.productUom}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {item.barcode || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">{item.quantity}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {editingItemId === item._id ? (
                                                        <div className="flex items-center">
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                max={item.quantity - (item.receivedQuantity || 0)}
                                                                value={receivedQuantities[item._id] || ''}
                                                                onChange={(e) => handleQuantityChange(item._id, e.target.value)}
                                                                className="w-20 border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-teal-500"
                                                                placeholder={`Max ${item.quantity - (item.receivedQuantity || 0)}`}
                                                            />
                                                            <span className="ml-2 text-sm text-gray-500">
                                                                (Remaining: {item.quantity - (item.receivedQuantity || 0)})
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            {item.receivedQuantity || 0} / {item.quantity}
                                                            {item.receivedQuantity > 0 && (
                                                                <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                                                                    item.receivedQuantity >= item.quantity
                                                                        ? 'bg-green-100 text-green-800'
                                                                        : 'bg-yellow-100 text-yellow-800'
                                                                }`}>
                                                                    {item.receivedQuantity >= item.quantity ? 'Complete' : 'Partial'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">{formatNumber(item.cost)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap font-medium">{formatNumber(item.quantity * item.cost)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap space-x-2">
                                                    {!item.isReceived && (
                                                        editingItemId === item._id ? (
                                                            <>
                                                                <button
                                                                    onClick={() => handleReceiveItem(item._id)}
                                                                    className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                                                                >
                                                                    Confirm
                                                                </button>
                                                                <button
                                                                    onClick={cancelEditing}
                                                                    className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <button
                                                                onClick={() => startEditing(item._id)}
                                                                className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                                                            >
                                                                Receive
                                                            </button>
                                                        )
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                            <div className="flex justify-end">
                                <div className="w-full md:w-1/3">
                                    <div className="flex justify-between mb-2">
                                        <span className="font-semibold">Subtotal:</span>
                                        <span>{formatNumber(po.total)}</span>
                                    </div>
                                    <div className="flex justify-between text-xl font-bold border-t pt-2">
                                        <span>Total:</span>
                                        <span>{formatNumber(po.total)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* Returns Section */}
                {(!isRetailTenant || activeTab === 'returns') && (
                    <>
                        {returnHistory.length > 0 && (
                            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                                <h2 className="text-xl font-semibold mb-4">Return Orders</h2>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RPO Number</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Processed By</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {returnHistory.map((returnOrder) => (
                                                <tr key={returnOrder._id}>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="font-medium text-orange-600">{returnOrder.poNumber}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(returnOrder.orderDate)}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{returnOrder.cashierId?.name || 'System'}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatNumber(returnOrder.total)}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{returnOrder.returnReason || 'No reason provided'}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <button
                                                            onClick={() => navigate(`/purchase-order/${returnOrder._id}`)}
                                                            className="text-blue-600 hover:text-blue-900"
                                                        >
                                                            View Details
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {po.status !== 'Cancelled' && (
                            <div className="flex justify-end space-x-4 mb-6">
                                {po.status === 'Received' && (
                                    <button
                                        onClick={handleReturnOrder}
                                        className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors disabled:opacity-50 no-print"
                                        disabled={loading}
                                    >
                                        {loading ? 'Processing...' : 'Return to Supplier'}
                                    </button>
                                )}
                                {po.status !== 'Received' && po.status !== 'Cancelled' && (
                                    <button
                                        onClick={handleCancel}
                                        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50 no-print"
                                        disabled={loading}
                                    >
                                        {loading ? 'Processing...' : 'Cancel PO'}
                                    </button>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* 3-Way Match Section (Retail only) */}
                {isRetailTenant && activeTab === 'match' && (
                    <div className="mb-6">
                        <ThreeWayMatchSummary key={summaryRefreshKey} poId={po._id} />
                    </div>
                )}

                {/* Return Modal */}
                {showReturnModal && (
                    <ReturnModal
                        items={selectedItems}
                        onClose={() => setShowReturnModal(false)}
                        onConfirm={handleProcessReturn}
                        loading={loading}
                        title="Return to Supplier"
                        confirmButtonText="Process Return"
                    />
                )}

                {/* Supplier Invoice Modal */}
                {showSupplierInvoiceModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                        <div className="bg-white w-full max-w-5xl rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center p-4 border-b">
                                <h3 className="text-lg font-semibold">Enter Supplier Invoice</h3>
                                <button
                                    onClick={() => setShowSupplierInvoiceModal(false)}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    Close
                                </button>
                            </div>
                            <div className="p-4">
                                <SupplierInvoiceForm
                                    poId={po._id}
                                    accountingClientId={selectedClient?._id || localStorage.getItem('accountingClientId')}
                                    onSuccess={handleSupplierInvoiceSuccess}
                                    onCancel={() => setShowSupplierInvoiceModal(false)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Credit Note Modal */}
                {showCreditNoteModal && creditNoteId && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                        <div className="bg-white w-full max-w-4xl rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center p-4 border-b">
                                <h3 className="text-lg font-semibold">Credit Note Review</h3>
                                <button
                                    onClick={() => setShowCreditNoteModal(false)}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    Close
                                </button>
                            </div>
                            <div className="p-4">
                                <CreditNoteApprovalCard
                                    creditNoteId={creditNoteId}
                                    onApproved={handleCreditNoteApproved}
                                    onRejected={handleCreditNoteApproved}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default PurchaseOrderDetailPage;